import React, {useRef, useState, useEffect} from "react";
import "./StreamFilter.css";

import InputGroup from "react-bootstrap/InputGroup";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Badge from "react-bootstrap/Badge";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBackwardStep, faForwardStep, faMagnifyingGlass, faSpinner, faPlus, faXmark, faAsterisk, faBan, faFilter,
} from "@fortawesome/free-solid-svg-icons";

import {useTimezone} from "../../../../../contexts/TimezoneContext";

const ATTRIBUTE_OPTIONS = [{value: 'all', label: 'all'}, {value: 'source', label: 'source'}, {
    value: 'action',
    label: 'action'
}, {value: 'payload.topic', label: 'MQTT topic'}, {value: 'payload.b', label: 'MQTT body'}, {
    value: 'payload.b.attr',
    label: 'MQTT body attr'
}, {value: 'payload.i', label: 'payload.i — request ID'},];

const DEBUG_LEVELS = [{value: 'minimal', label: 'Minimal', title: 'Only user events'}, {
    value: 'debug',
    label: 'Default',
    title: 'Hide noisy MQTT (m=0, topic=/)'
}, {value: 'all', label: 'All', title: 'No preset filtering'},];

const DEBUG_LEVEL_VARIANT = {
    minimal: 'warning', debug: 'info', all: 'success',
};

function StreamFilter({
                          filters,
                          onAddFilter,
                          onRemoveFilter,
                          onToggleFilter,
                          debugLevel,
                          onDebugLevelChange,
                          onDateRangeFilter,
                          bondIds,
                          queryLimit,
                          onQueryLimitChange
                      }) {
    const {timezone, formatDateCustom, parseDateInTimezone} = useTimezone();
    const [inputText, setInputText] = useState('');
    const [attributeText, setAttributeText] = useState('');
    const [isRegex, setIsRegex] = useState(false);
    const [isNegative, setIsNegative] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const startDatePickerRef = useRef();
    const [currentStartDate, setCurrentStartDate] = useState(null);
    const endDatePickerRef = useRef();
    const [currentEndDate, setCurrentEndDate] = useState(null);
    const prevBondIdsRef = useRef();

    useEffect(() => {
        const prevBondIds = prevBondIdsRef.current;
        const currentBondIds = bondIds?.join(',') || '';

        if (prevBondIds && prevBondIds !== currentBondIds) {
            setCurrentStartDate(null);
            setCurrentEndDate(null);
        }

        prevBondIdsRef.current = currentBondIds;
    }, [bondIds]);

    const formatDateTimeLocal = (date) => {
        if (!date) return '';
        try {
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone: timezone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });
            const parts = formatter.formatToParts(date);
            const f = {};
            parts.forEach(p => {
                if (p.type !== 'literal') f[p.type] = p.value;
            });
            return `${f.year}-${f.month}-${f.day}T${f.hour}:${f.minute}`;
        } catch {
            return '';
        }
    };

    const formatDateDisplay = (date) => {
        if (!date) return null;
        return formatDateCustom(date, 'yyyy-MM-dd HH:mm');
    };

    const handleAddFilter = () => {
        if (!inputText.trim()) return;
        const attribute = attributeText.trim() || 'any';
        const attrDisplay = attribute === 'any' ? null : attribute;
        const label = attrDisplay ? `${attrDisplay}: ${inputText}` : inputText;
        onAddFilter({
            id: Date.now().toString(),
            query: inputText.trim(),
            is_regex: isRegex,
            is_negative: isNegative,
            attribute,
            enabled: true,
            label,
        });
        setInputText('');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleAddFilter();
    };

    const handleDateRangeSubmit = async () => {
        if (!onDateRangeFilter) return;

        if (currentStartDate && currentEndDate) {
            const diffDays = Math.ceil(Math.abs(currentEndDate - currentStartDate) / (1000 * 60 * 60 * 24));
            if (diffDays > 60) {
                alert(`Date range cannot exceed 2 months (60 days). Selected range: ${diffDays} days.`);
                return;
            }
        }

        setIsLoading(true);
        try {
            await onDateRangeFilter(currentStartDate, currentEndDate);
        } catch (error) {
            console.error('Failed to filter events by date range:', error);
        } finally {
            setIsLoading(false);
        }
    };

    return (<div className="mb-2">
            <div className="d-flex gap-2 flex-wrap mb-1">
                <div className="d-flex align-items-center gap-2 flex-grow-1 flex-nowrap">
                    <InputGroup size="md">
                        <InputGroup.Text>
                            Query
                        </InputGroup.Text>
                        <Form.Control
                            type="datetime-local"
                            ref={startDatePickerRef}
                            className="streamDatetimePicker"
                            value={formatDateTimeLocal(currentStartDate || currentEndDate || null)}
                            max={formatDateTimeLocal(currentEndDate ? currentEndDate : new Date())}
                            min={currentEndDate ? (() => {
                                const d = new Date(currentEndDate);
                                d.setDate(d.getDate() - 60);
                                return formatDateTimeLocal(d);
                            })() : null}
                            onChange={(e) => setCurrentStartDate(parseDateInTimezone(e.target.value))}
                        />
                        <Button
                            variant={currentStartDate ? 'secondary' : 'outline-secondary'}
                            size="sm"
                            onClick={() => startDatePickerRef.current.showPicker()}
                            style={{minWidth: '140px'}}
                        >
                            <FontAwesomeIcon icon={faBackwardStep} className="me-1"/>
                            {currentStartDate ? formatDateDisplay(currentStartDate) : 'Start'}
                        </Button>

                        <Form.Control
                            type="datetime-local"
                            ref={endDatePickerRef}
                            className="streamDatetimePicker"
                            value={formatDateTimeLocal(currentEndDate || currentStartDate || null)}
                            min={formatDateTimeLocal(currentStartDate)}
                            max={formatDateTimeLocal(currentStartDate ? (() => {
                                const d = new Date(currentStartDate);
                                d.setDate(d.getDate() + 60);
                                const now = new Date();
                                return d < now ? d : now;
                            })() : new Date())}
                            onChange={(e) => setCurrentEndDate(parseDateInTimezone(e.target.value))}
                        />
                        <Button
                            variant={currentEndDate ? 'secondary' : 'outline-secondary'}
                            size="sm"
                            onClick={() => endDatePickerRef.current.showPicker()}
                            style={{minWidth: '140px'}}
                        >
                            {currentEndDate ? formatDateDisplay(currentEndDate) : 'End'}
                            <FontAwesomeIcon icon={faForwardStep} className="ms-1"/>
                        </Button>
                        <Form.Select
                            size="sm"
                            value={queryLimit}
                            onChange={(e) => onQueryLimitChange(e.target.value)}
                            style={{maxWidth: '120px'}}
                            title="Query limit"
                        >
                            <option value={10}>10</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={500}>500</option>
                            <option value={1000}>1000</option>
                            <option value={10000}>10000</option>
                            <option value={100000}>100000</option>
                        </Form.Select>
                        <Button
                            variant="primary"
                            size="md"
                            onClick={handleDateRangeSubmit}
                            disabled={(!currentStartDate && !currentEndDate) || isLoading}
                            title="Search events in date range"
                        >
                            <FontAwesomeIcon icon={isLoading ? faSpinner : faMagnifyingGlass}
                                             className={isLoading ? 'fa-spin' : ''}/>
                        </Button>
                    </InputGroup>

                    {/* Debug level preset — pushed to the right */}
                    <ButtonGroup size="sm" className="ms-auto flex-shrink-0">
                        {DEBUG_LEVELS.map(({value, label, title}) => (<Button
                                key={value}
                                variant={debugLevel === value ? DEBUG_LEVEL_VARIANT[value] : 'outline-secondary'}
                                onClick={() => onDebugLevelChange(value)}
                                title={title}
                                style={{minWidth: '80px'}}
                            >
                                {label}
                            </Button>))}
                    </ButtonGroup>
                </div>

                <InputGroup size="sm" className="filterBuilderGroup">
                    <InputGroup.Text>
                        <FontAwesomeIcon icon={faFilter}/>
                    </InputGroup.Text>
                    <Form.Control
                        size="sm"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Filter Events"
                    />
                    <Form.Control
                        size="sm"
                        value={attributeText}
                        onChange={(e) => setAttributeText(e.target.value)}
                        placeholder="attribute (any)"
                        list="filterAttributeSuggestions"
                        className="filterAttributeInput text-light"
                    />
                    <datalist id="filterAttributeSuggestions">
                        {ATTRIBUTE_OPTIONS.map(opt => (<option key={opt.value} value={opt.value}>{opt.label}</option>))}
                    </datalist>
                    <Button
                        variant={isRegex ? 'secondary' : 'outline-secondary'}
                        size="sm"
                        onClick={() => setIsRegex(r => !r)}
                        title={isRegex ? 'Regex: on' : 'Regex: off'}
                        className="filterToggleBtn"
                    >
                        <FontAwesomeIcon icon={faAsterisk}/>
                    </Button>
                    <Button
                        variant={isNegative ? 'danger' : 'outline-danger'}
                        size="sm"
                        onClick={() => setIsNegative(n => !n)}
                        title={isNegative ? 'Exclude: on' : 'Exclude: off'}
                        className="filterToggleBtn"
                    >
                        <FontAwesomeIcon icon={faBan}/>
                    </Button>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={handleAddFilter}
                        disabled={!inputText.trim()}
                        title="Add filter (Enter)"
                    >
                        <FontAwesomeIcon icon={faPlus}/>
                    </Button>
                </InputGroup>
            </div>

            {/* Row 2: active filter badges */}
            {filters && filters.length > 0 && (<div className="filterBadgesArea">
                    {filters.map(filter => (<span
                            key={filter.id}
                            className={`filterBadge${filter.enabled ? '' : ' disabled'}`}
                            onClick={() => onToggleFilter(filter.id)}
                            title={`${filter.is_negative ? 'Exclude' : 'Include'} · ${filter.attribute} · ${filter.is_regex ? 'regex' : 'text'}: ${filter.query}`}
                        >
                            <Badge
                                bg={filter.is_negative ? 'danger' : 'primary'}
                                style={{fontSize: '0.9rem !important', fontWeight: 500}}
                            >
                                {filter.is_negative && (
                                    <FontAwesomeIcon icon={faBan} size="s" className="me-1" style={{opacity: 0.8}}/>)}
                                {filter.label}
                                {filter.is_regex && (<FontAwesomeIcon icon={faAsterisk} size="s" className="ms-1"
                                                                      style={{opacity: 0.9 }}/>)}
                            </Badge>
                            <button
                                className="filterBadgeRemove"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemoveFilter(filter.id);
                                }}
                                title="Remove filter"
                            >
                                <FontAwesomeIcon icon={faXmark} size="xs"/>
                            </button>
                        </span>))}
                </div>)}
        </div>);
}

export default StreamFilter;
